import React, { useEffect, useState } from 'react';
import { BriefcaseBusiness, EllipsisVertical, ShoppingCart, UserRound, UsersRound } from 'lucide-react';
import { TrendingUp } from "lucide-react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Bar, BarChart } from "recharts";
import { Label, PolarRadiusAxis, RadialBar, RadialBarChart } from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../components/ui/chart";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const Dashboard = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("adminToken");
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [partners, setPartners] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [subServicesCount, setSubServicesCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState({ permissions: {} });

  // Helper function to render cards with conditional links
  const renderCardWithLink = (to, permissionKey, title, count, icon) => {
    const hasPermission = admin.permissions[permissionKey];
    
    const cardContent = (
      <Card className='h-[100px] flex justify-between p-3 border-b-4 border-blue-300'>
        <div>
          <h1 className='text-xl'>{title}</h1>
          <span className='text-4xl font-bold'>{count}</span>
        </div>
        <div className='bg-blue-100 size-10 flex justify-center items-center rounded-full'>
          {icon}
        </div>
      </Card>
    );

    return hasPermission ? (
      <Link to={to} className="block h-full w-full">
        {cardContent}
      </Link>
    ) : (
      <div className="block h-full w-full cursor-not-allowed opacity-75">
        {cardContent}
      </div>
    );
  };

  // Helper function for chart cards with conditional links
  const renderChartCardWithLink = (to, permissionKey, children) => {
    const hasPermission = admin.permissions[permissionKey];
    
    return hasPermission ? (
      <Link to={to} className="block h-full w-full">
        {children}
      </Link>
    ) : (
      <div className="block h-full w-full cursor-not-allowed opacity-75">
        {children}
      </div>
    );
  };

  const fetchAdmin = async () => {
    try {
      const res = await axios.get(
        "https://wavetechservices.in/api/admin/profile",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setAdmin(res?.data?.admin);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("adminToken");
        toast.error("Authentication token verification failed please login.");
        setTimeout(() => {
          window.location.assign("/admin/login");
        }, 1000);
      }
      console.error("Error fetching admin data:", err);
    }
  };

  // Process booking data to get monthly counts and revenue
  const processBookingData = (bookings) => {
    const monthNames = ["January", "February", "March", "April", "May", "June", 
                       "July", "August", "September", "October", "November", "December"];
    
    // Initialize monthly data structure
    const monthlyCounts = Array(12).fill(0).map((_, i) => ({
      month: monthNames[i],
      count: 0,
      revenue: 0
    }));
    
    // Process each booking
    bookings.forEach(booking => {
      const date = new Date(booking.createdAt);
      const month = date.getMonth();
      
      if (month >= 0 && month < 12) {
        monthlyCounts[month].count += 1;
        monthlyCounts[month].revenue += booking.amount || 0;
      }
    });
    
    return monthlyCounts;
  };

  // Get last 6 months data for charts
  const getLastSixMonthsData = (processedData) => {
    const currentMonth = new Date().getMonth();
    const lastSixMonths = [];
    
    for (let i = 5; i >= 0; i--) {
      let monthIndex = (currentMonth - i + 12) % 12;
      lastSixMonths.push({
        ...processedData[monthIndex],
        month: processedData[monthIndex].month.slice(0, 3) // Short month name
      });
    }
    
    return lastSixMonths;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchAdmin();
        
        const [usersRes, servicesRes, bookingsRes, partnersRes, subServicesRes] = await Promise.all([
          axios.get("https://wavetechservices.in/api/admin/users", {
            headers: {
              "Authorization": `Bearer ${token}`
            }
          }),
          axios.get("https://wavetechservices.in/api/user/services"),
          axios.get("https://wavetechservices.in/admin/bookings", {
            headers: {
              "Authorization": `Bearer ${token}`
            }
          }),
          axios.get("https://wavetechservices.in/api/admin/partners", {
            headers: {
              "Authorization": `Bearer ${token}`
            }
          }),
          axios.get("https://wavetechservices.in/api/admin/services/sub-services", {
            headers: {
              "Authorization": `Bearer ${token}`
            }
          })
        ]);

        setUsers(usersRes.data.data);
        setServices(servicesRes.data.data);
        setBookings(bookingsRes.data.data);
        setPartners(partnersRes.data.partners);
        setSubServicesCount(subServicesRes.data?.count);
        
        // Process booking data for charts
        const processedData = processBookingData(bookingsRes.data.data);
        const lastSixMonths = getLastSixMonthsData(processedData);
        
        setMonthlyData(lastSixMonths.map(item => ({
          month: item.month,
          bookings: item.count
        })));
        
        setRevenueData(lastSixMonths.map(item => ({
          month: item.month,
          revenue: item.revenue
        })));
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Chart configurations
  const bookingsChartConfig = {
    bookings: {
      label: "Bookings",
      color: "#0a67f2",
    },
  };

  const revenueChartConfig = {
    revenue: {
      label: "Revenue",
      color: "#0a67f2",
    },
  };

  const statsChartConfig = {
    completed: {
      label: "Completed",
      color: "#0a67f2",
    },
    pending: {
      label: "Pending",
      color: "#fd2692",
    },
  };

  // Calculate booking statistics for radial chart
  const getBookingStats = () => {
    if (bookings.length === 0) return [{ completed: 0, pending: 0 }];
    
    const completed = bookings.filter(b => b.status === 'completed').length;
    const pending = bookings.filter(b => b.status === 'pending').length;
    
    return [{
      completed,
      pending,
      total: bookings.length
    }];
  };

  // Calculate growth percentage
  const calculateGrowth = (data, key) => {
    if (data.length < 2) return 0;
    
    const current = data[data.length - 1][key] || 0;
    const previous = data[data.length - 2][key] || 0;
    
    if (previous === 0) return 0;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  const bookingGrowth = calculateGrowth(monthlyData, 'bookings');
  const revenueGrowth = calculateGrowth(revenueData, 'revenue');

  if (loading) {
    return<div className="flex items-center justify-center h-screen">
           <button
  disabled=""
  class="bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold py-3 px-6 rounded-full shadow-lg flex items-center transition duration-300 transform hover:scale-105 active:scale-95"
>
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    class="animate-spin h-5 w-5 mr-3 text-white"
  >
    <circle
      stroke-width="4"
      stroke="currentColor"
      r="10"
      cy="12"
      cx="12"
      class="opacity-25"
    ></circle>
    <path
      d="M4 12a8 8 0 018-8v8H4z"
      fill="currentColor"
      class="opacity-75"
    ></path>
  </svg>
  Loading...
</button>
    </div>

  }
 
  if (!token) {
    navigate("/admin/login"); 
  } 
  
  return (
    <div className='p-3'>
      <div className='flex flex-col lg:flex-row gap-4 mx-auto'>
        {/* Customer Card */}
        <div className="lg:w-[32%] w-full">
          {renderCardWithLink(
            "/admin/user",
            "customer",
            "Customer",
            users.length,
            <UserRound className='rounded-full text-blue-800 size-5' />
          )}
        </div>
        
        {/* Provider Card */}
        <div className="lg:w-[32%] w-full">
          {renderCardWithLink(
            "/admin/applied_provider",
            "verifiedProvider",
            "Provider",
            partners.length,
            <UsersRound className='rounded-full text-blue-800 size-5' />
          )}
        </div>
        
        {/* Service Card */}
        <div className="lg:w-[32%] w-full">
          {renderCardWithLink(
            "/admin/subservice",
            "subServices",
            "Service",
            subServicesCount,
            <BriefcaseBusiness className='rounded-full text-blue-800 size-5' />
          )}
        </div>
        
        {/* Booking Card */}
        <div className="lg:w-[32%] w-full">
          {renderCardWithLink(
            "/admin/booking",
            "booking",
            "Booking",
            bookings.length,
            <ShoppingCart className='rounded-full text-blue-800 size-5' />
          )}
        </div>
      </div>

      <div className='my-3 flex gap-4 flex-col lg:flex-row'>
        {/* Bookings Chart */}
        <div className='lg:w-[33%] w-full'>
          {renderChartCardWithLink(
            "/admin/sales",
            "sales",
            <Card className='h-full'>
              <CardHeader>
                <div className='flex justify-between items-center'>
                  <div>
                    <CardTitle>Total Booking</CardTitle>
                    <CardDescription>
                      Showing total bookings for the last 6 months
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ChartContainer config={bookingsChartConfig}>
                  <AreaChart
                    accessibilityLayer
                    data={monthlyData}
                    margin={{ left: 12, right: 12 }}
                  >
                    <CartesianGrid vertical={false} />
                    <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} />
                    <YAxis hide />
                    <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" />} />
                    <Area
                      dataKey="bookings"
                      type="natural"
                      fill="var(--color-bookings)"
                      fillOpacity={0.4}
                      stroke="var(--color-bookings)"
                    />
                  </AreaChart>
                </ChartContainer>
              </CardContent>
              <CardFooter>
                <div className="flex w-full items-start gap-2 text-sm">
                  <div className="grid gap-2">
                    <div className="flex items-center gap-2 font-medium leading-none">
                      {bookingGrowth >= 0 ? 'Trending up' : 'Trending down'} by {Math.abs(bookingGrowth)}% this month 
                      {bookingGrowth >= 0 ? 
                        <TrendingUp className="h-4 w-4 text-green-500" /> : 
                        <TrendingUp className="h-4 w-4 text-red-500 transform rotate-180" />}
                    </div>
                    <div className="flex items-center gap-2 leading-none text-muted-foreground">
                      Last 6 months
                    </div>
                  </div>
                </div>
              </CardFooter>
            </Card>
          )}
        </div>

        {/* Revenue Chart */}
        <div className='lg:w-[33%] w-full'>
          {renderChartCardWithLink(
            "/admin/revenue",
            "revenue",
            <Card className='h-full'>
              <CardHeader>
                <div className='flex justify-between items-center'>
                  <div className='w-[75%]'>
                    <CardTitle>Total Revenue</CardTitle>
                    <CardDescription>Last 6 months</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ChartContainer config={revenueChartConfig}>
                  <BarChart accessibilityLayer data={revenueData}>
                    <CartesianGrid vertical={false} />
                    <XAxis dataKey="month" tickLine={false} tickMargin={10} axisLine={false} />
                    <YAxis hide />
                    <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                    <Bar dataKey="revenue" fill="var(--color-revenue)" radius={8} />
                  </BarChart>
                </ChartContainer>
              </CardContent>
              <CardFooter className="flex-col items-start gap-2 text-sm">
                <div className="flex gap-2 font-medium leading-none">
                  {revenueGrowth >= 0 ? 'Trending up' : 'Trending down'} by {Math.abs(revenueGrowth)}% this month 
                  {revenueGrowth >= 0 ? 
                    <TrendingUp className="h-4 w-4 text-green-500" /> : 
                    <TrendingUp className="h-4 w-4 text-red-500 transform rotate-180" />}
                </div>
                <div className="leading-none text-muted-foreground">
                  Showing total revenue for the last 6 months
                </div>
              </CardFooter>
            </Card>
          )}
        </div>

        {/* Booking Statistics Chart */}
        <div className='lg:w-[33%] w-full'>
          {renderChartCardWithLink(
            "/admin/booking",
            "booking",
            <Card className="flex flex-col w-full h-full cursor-pointer hover:shadow-md transition-shadow">
              <CardHeader className="items-center pb-0">
                <CardTitle>Booking Statistics</CardTitle>
                <CardDescription>Status overview</CardDescription>
              </CardHeader>

              <CardContent className="flex flex-1 items-center pb-0">
                <ChartContainer
                  config={statsChartConfig}
                  className="mx-auto aspect-square w-full max-w-[250px]"
                >
                  <RadialBarChart
                    data={getBookingStats()}
                    endAngle={180}
                    innerRadius={80}
                    outerRadius={130}
                  >
                    <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                    <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
                      <Label
                        content={({ viewBox }) => {
                          if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                            return (
                              <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle">
                                <tspan
                                  x={viewBox.cx}
                                  y={(viewBox.cy || 0) - 16}
                                  className="fill-foreground text-2xl font-bold"
                                >
                                  {bookings.length}
                                </tspan>
                                <tspan
                                  x={viewBox.cx}
                                  y={(viewBox.cy || 0) + 4}
                                  className="fill-muted-foreground"
                                >
                                  Bookings
                                </tspan>
                              </text>
                            )
                          }
                        }}
                      />
                    </PolarRadiusAxis>
                    <RadialBar
                      dataKey="completed"
                      stackId="a"
                      cornerRadius={5}
                      fill="var(--color-completed)"
                      className="stroke-transparent stroke-2"
                    />
                    <RadialBar
                      dataKey="pending"
                      fill="var(--color-pending)"
                      stackId="a"
                      cornerRadius={5}
                      className="stroke-transparent stroke-2"
                    />
                  </RadialBarChart>
                </ChartContainer>
              </CardContent>

              <CardFooter className="flex-col gap-2 text-sm">
                <div className="flex items-center gap-2 font-medium leading-none">
                  {getBookingStats()[0].completed} completed, {getBookingStats()[0].pending} pending
                </div>
                <div className="leading-none text-muted-foreground">
                  Current booking status distribution
                </div>
              </CardFooter>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;